import { ChangeDetectionStrategy, Component, Input, OnInit, output, Signal } from '@angular/core';
import { Store } from '@ngrx/store';

import { StudioTemplateConfigurations } from '../../configs/studio-template-configs';
import { StudioTemplateConfigCategory } from '../../models/studio-template-model';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { MediasPublicConstants } from '@features/products/medias';
import { ProductPublicModels } from '@features/products/product';
import { Resolutions } from '@layout/domain/models/layout.models';
import { getResolution } from '@layout/infrastructure/state/layout.selectors';
import { getCatalogStyles } from '@pages/category/store/category-page.selectors';
import { CategoryConfiguration, GridProductStyle, HiddenFieldType, ProductViewConfig } from '@shared/product/models';

@Component({
    selector: 'studio-templates',
    templateUrl: './studio-templates.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StudioTemplatesComponent implements OnInit {
    @Input() public categoryId: string;

    @Input() public products: number[] = [];

    @Input() public categoryConfiguration: CategoryConfiguration;

    @Input() public templateName: string;

    @Input() public place: string;

    @Input() public elementIndex: number;

    @Input() public elementDefaultImageTypes: Map<number, string>;

    @Input() public isMediaCarouselActivated: boolean;

    @Input() public defaultTemplateAttachment: boolean;

    @Input() public productStyle: GridProductStyle;

    public readonly clickAddToCart = output<ProductPublicModels.PublicProduct>();

    public studioTemplateConfig: ProductViewConfig;

    public templateNameFirst: string;

    public skeletonClass: string;

    public skeletonNoDetails: string;

    public isMobile$$: Signal<Resolutions> = this.store.selectSignal(getResolution);

    public catalogStyles$$ = this.store.selectSignal(getCatalogStyles);

    constructor(
        private readonly store: Store,
        private readonly studioTemplateConfigurations: StudioTemplateConfigurations,
        private readonly platformService: CorePlatformService,
    ) { }

    public ngOnInit(): void {
        if (this.templateName === 'CC') {
            this.templateName = this.defaultTemplateAttachment ? 'N4A_1' : 'N2C_1';
        }
        this.getTemplatePrefix();
        const hideField = this.findCommonHiddenField();
        this.setSkeletonClass(hideField);
        const configCategoryForced = this.configsCategoryForced(hideField);
        this.studioTemplateConfig = this.studioTemplateConfigurations.getStudioTemplateConfig(
            this.templateNameFirst,
            configCategoryForced
        );

        if (!this.studioTemplateConfig) return;

        this.setCatalogStyles();

        // Medida provisional para mantener funcionalidad de mobile y desk para imagenes, en SSR esto supone un flickering (el server debería saber quien es el cliente¿?)
        const hasDesktop = this.platformService.isBrowser() && this.isMobile$$() !== Resolutions.MOBILE;
        const templateAdaptative = this.templateNameFirst === 'N1F' || this.templateNameFirst === 'N1A'; // SSR es 100% responsive, esto no se debe hacer
        if (templateAdaptative && hasDesktop) {
            this.studioTemplateConfig.location = MediasPublicConstants.PublicMediaLocationType.category_page;
        }
        this.setConfigShowMediaCarousel(hasDesktop);
    }

    public setConfigShowMediaCarousel(hasDesktop: boolean) {
        if (!(this.isMediaCarouselActivated && hasDesktop)) {
            this.studioTemplateConfig.showMediaCarousel = this.isMediaCarouselActivated
                ? this.studioTemplateConfig.showMediaCarouselMobile
                : false;
        }
    }

    public addToCartSheet({ product }) {
        this.clickAddToCart.emit(product);
    }

    private configsCategoryForced(hideField: HiddenFieldType): StudioTemplateConfigCategory {
        return {
            showDetails: hideField === HiddenFieldType.ALL ? false : null,
            showDescription: hideField === HiddenFieldType.DESCRIPTION ? false : null,
            showPrice: hideField === HiddenFieldType.PRICE ? false : null,
        };
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public getTemplatePrefix(): void {
        const parts = this.templateName.split('_');
        this.templateNameFirst = parts.length > 1 ? parts[0] : this.templateName;
        this.replaceTemplateNameMobile(parts);
    }

    // TODO ECOMDUTI-22438 [TechDebt] - Fix de ultima hora para cumplir con una peticion de negocio, los templates N1F se deberían gestionar por CSS
    private replaceTemplateNameMobile(parts): void {
        if (this.templateNameFirst === 'N1F' && this.isMobile$$() === Resolutions.MOBILE) {
            this.templateNameFirst = 'N1A';
            this.templateName = `${this.templateNameFirst}_${parts[1]}`;
        }
    }

    private findCommonHiddenField(): HiddenFieldType | undefined {
        const hiddenFieldsProducts = this.products.map(productId =>
            this.categoryConfiguration.productConf.get(productId)?.hiddenField);
        const firstElement = hiddenFieldsProducts?.pop();

        const isSameFirst = hiddenFieldsProducts.every(element => element === firstElement);
        return isSameFirst ? firstElement : undefined;
    }

    public setSkeletonClass(hiddenField: HiddenFieldType): void {
        const skload5 = ['n1c', 'n1f', 'n1j'];
        this.skeletonClass = skload5.includes(this.templateNameFirst.toLowerCase()) ? 'sk-load5' : 'sk-load2';

        if (hiddenField) {
            this.setSkeletonNoDetails(hiddenField);
        }
    }

    private setSkeletonNoDetails(hiddenField: HiddenFieldType): void {
        const skeletonMap = {
            all: 'sk-nodetails',
            description: 'sk-nodescription',
        };

        this.skeletonNoDetails = skeletonMap[hiddenField] || '';
    }

    private setCatalogStyles(): void {
        if (this.catalogStyles$$()) {
            this.studioTemplateConfig.styles = {
                backgroundColor: this.catalogStyles$$().backgroundColor,
                fontColor: this.catalogStyles$$().fontColor,
                offerPrice: this.catalogStyles$$().offerPrice,
                colorSelector: this.catalogStyles$$().colorSelector,
                cardColor: this.catalogStyles$$().backgroundColor,
            };
        }
    }
}
